import { Mongo } from 'meteor/mongo';
import { trimPhone } from 'server/shared/helpers/trimPhoneHelper';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { TranscribeJob } from 'server/shared/models/transcribeJob';
import { TranslationKeyTranslation } from 'server/shared/models/translation';
import { getMatchingShiftsForSearchText } from 'server/shared/helpers/jobs-query/getJobsMongoQuery';

export enum TranscribeJobListSearchInFieldEnum {
  ALL = 'ALL',
  DESCRIPTION = 'DESCRIPTION',
  USER = 'USER',
  BOOKED_USER = 'BOOKED_USER',
  CANCELED_USER = 'CANCELED_USER',
  SHIFT = 'SHIFT',
}

/**
 * Helper function to get search query for TranscribeJob based on search type and text
 * Note: Access control is handled by the publication, so we don't need to filter by user/customer here
 */
export function getTranscribeJobSearchQuery(
  searchText: string,
  searchType: TranscribeJobListSearchInFieldEnum,
  i18n?: TranslationKeyTranslation,
): Mongo.Selector<TranscribeJob> | undefined {
  if (!searchText || searchText.trim().length === 0) {
    return undefined;
  }

  // Escape special regex characters
  let escapedSearchText = searchText.replace(/\(/g, '\\(');
  escapedSearchText = escapedSearchText.replace(/\[/g, '\\[');
  escapedSearchText = escapedSearchText.replace(/\)/g, '\\)');
  escapedSearchText = escapedSearchText.replace(/\+/g, '\\+');

  const matchingShifts = getMatchingShiftsForSearchText(searchText, i18n);
  const trimmedPhone = trimPhone(searchText).replace('+', '\\+');
  const searchTexts = escapedSearchText.split(' ');

  let searchQuery: any[] = [];

  switch (searchType) {
    case TranscribeJobListSearchInFieldEnum.ALL: {
      // Search in invitee personal data
      searchTexts.forEach((text) => {
        searchQuery.push({
          $or: [
            {
              'invitee.personalData.firstName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.lastName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.email': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.phone': {
                $regex: text,
                $options: 'i',
              },
            },
          ],
        });
      });
      if (trimmedPhone && trimmedPhone.length > 3) {
        searchQuery.push({
          'invitee.personalData.phone': {
            $regex: trimmedPhone,
            $options: 'i',
          },
        });
      }

      // Search in shifts
      searchQuery.push(
        {
          _id: searchText,
        },
        {
          'shifts.label': {
            $regex: escapedSearchText,
            $options: 'i',
          },
        },
      );

      if (matchingShifts.length > 0) {
        searchQuery.push({
          'shifts.label': { $exists: false },
          'shifts.shift': { $in: matchingShifts },
        });
      }
      break;
    }
    case TranscribeJobListSearchInFieldEnum.USER: {
      searchTexts.forEach((text) => {
        searchQuery.push({
          $or: [
            {
              'invitee.personalData.firstName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.lastName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.email': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.phone': {
                $regex: text,
                $options: 'i',
              },
            },
          ],
        });
      });
      if (trimmedPhone && trimmedPhone.length > 3) {
        searchQuery.push({
          'invitee.personalData.phone': {
            $regex: trimmedPhone,
            $options: 'i',
          },
        });
      }
      break;
    }
    case TranscribeJobListSearchInFieldEnum.DESCRIPTION:
      searchQuery = [
        {
          description: {
            $regex: escapedSearchText,
            $options: 'i',
          },
        },
      ];
      break;
    case TranscribeJobListSearchInFieldEnum.BOOKED_USER:
      searchTexts.forEach((text) => {
        searchQuery.push({
          'invitee.status': InviteeStatusEnum.BOOKED,
          $or: [
            {
              'invitee.personalData.firstName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.lastName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.email': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.phone': {
                $regex: text,
                $options: 'i',
              },
            },
          ],
        });
      });
      if (trimmedPhone && trimmedPhone.length > 3) {
        searchQuery.push({
          'invitee.status': InviteeStatusEnum.BOOKED,
          'invitee.personalData.phone': {
            $regex: trimmedPhone,
            $options: 'i',
          },
        });
      }
      break;
    case TranscribeJobListSearchInFieldEnum.CANCELED_USER:
      searchTexts.forEach((text) => {
        searchQuery.push({
          'invitee.status': InviteeStatusEnum.CANCELED,
          $or: [
            {
              'invitee.personalData.firstName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.lastName': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.email': {
                $regex: text,
                $options: 'i',
              },
            },
            {
              'invitee.personalData.phone': {
                $regex: text,
                $options: 'i',
              },
            },
          ],
        });
      });
      if (trimmedPhone && trimmedPhone.length > 3) {
        searchQuery.push({
          'invitee.status': InviteeStatusEnum.CANCELED,
          'invitee.personalData.phone': {
            $regex: trimmedPhone,
            $options: 'i',
          },
        });
      }
      break;
    case TranscribeJobListSearchInFieldEnum.SHIFT:
      searchQuery = [
        {
          'shifts.label': {
            $regex: escapedSearchText,
            $options: 'i',
          },
        },
      ];
      if (matchingShifts.length > 0) {
        searchQuery.push({
          'shifts.label': { $exists: false },
          'shifts.shift': { $in: matchingShifts },
        });
      }
      break;
  }

  if (searchQuery.length === 0) {
    return undefined;
  }

  return {
    $or: searchQuery,
  };
}
